import { useCallback, useEffect, useRef } from 'react';
import { useDocumentTabSettings } from './useDocumentTabSettings';
import { UISettings } from '../../libs/ui-settings-manager/interfaces';

export const useTabIndexManager = (
    documentKey: keyof UISettings['documents'], 
    containerRef?: React.RefObject<HTMLElement>,
    
) => {
    const { settings } = useDocumentTabSettings(documentKey);
    const hasFocused = useRef(false);
    
    const getSearchRoot = useCallback(() => {
        return containerRef?.current || document.body;
    }, [containerRef]);

    const focusOnTabIndexOne = useCallback(() => {
        if (hasFocused.current) {
            return false;
        }
        
        const searchRoot = getSearchRoot();
        const element = searchRoot.querySelector('[tabindex="1"]') as HTMLElement;
        
        if (!element) {
            return false;
        }
        
        const isInOurContainer = containerRef?.current 
            ? containerRef.current.contains(element)
            : true;
        
        if (!isInOurContainer) {
            return false;
        }
        
        const isFocusable = 
            !element.hasAttribute('disabled') &&
            element.getAttribute('aria-disabled') !== 'true' &&
            element.offsetParent !== null &&
            window.getComputedStyle(element).display !== 'none' &&
            window.getComputedStyle(element).visibility !== 'hidden';
        
        if (!isFocusable) {
            return false;
        }
        
        try {
            element.focus();
            hasFocused.current = true;
            
            return true;
        } catch (error) {
            console.error('Ошибка при установке фокуса:', error);
            return false;
        }
    }, [containerRef, getSearchRoot]);

    const applyAndFocus = useCallback(() => {
        
        const searchRoot = getSearchRoot();
        
        settings.tabOrder.forEach((fieldId, index) => {
            if (!settings.enabledFields.includes(fieldId)) {
                return;
            }
            
            const tabIndexValue = index + 1;
            
            const element = searchRoot.querySelector(
                `[data-field-id="${fieldId}"]`
            ) as HTMLElement;
            
            if (element) {
                const isInOurContainer = containerRef?.current 
                    ? containerRef.current.contains(element)
                    : true;
                
                if (isInOurContainer) {
                    element.setAttribute('tabindex', String(tabIndexValue));
                }
            } 
        });
        
        searchRoot.querySelectorAll('[data-field-id]').forEach(element => {
            const isInOurContainer = containerRef?.current 
                ? containerRef.current.contains(element)
                : true;
            
            if (!isInOurContainer) {
                return;
            }
            
            const fieldId = element.getAttribute('data-field-id');
            if (!fieldId) return;
            
            const htmlElement = element as HTMLElement;
            
            if (!settings.enabledFields.includes(fieldId)) {
                htmlElement.setAttribute('tabindex', '-1');
            }
        });
        
    }, [settings, containerRef, focusOnTabIndexOne, getSearchRoot]);
    
    useEffect(() => {
        hasFocused.current = false;
        
        const timers = [
            setTimeout(() => {applyAndFocus()}, 200),
            setTimeout(() => {applyAndFocus()}, 500),
            setTimeout(() => {applyAndFocus()}, 1000)
        ];
        
        const observer = new MutationObserver(() => {
            if (!hasFocused.current) {
                setTimeout(focusOnTabIndexOne, 100);
            }
        });
        
        const observeTarget = containerRef?.current || document.body;
        observer.observe(observeTarget, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['tabindex', 'data-field-id', 'disabled']
        });
        
        return () => {
            timers.forEach(timer => clearTimeout(timer));
            observer.disconnect();
        };
    }, [applyAndFocus, focusOnTabIndexOne, documentKey, containerRef]);
    
    return { 
        applyAndFocus, 
        focusOnTabIndexOne,
        hasFocused: hasFocused.current 
    };
};